/**
 * Image URL utilities for Tourvisio API images
 * Based on tourvisio-web reference implementation
 */

const B2C_BASE = 'https://lumitours.se/api/media-cache';

interface ImageOptions {
    width?: number;
    height?: number;
    mode?: 'pad' | 'boxpad' | 'crop' | 'min' | 'max' | 'stretch' | 'contain' | 'cover';
    anchor?: 'top' | 'bottom' | 'center' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
    quality?: number;
    bgcolor?: string;
    widthratio?: number;
    heightratio?: number;
}

const getMediaBaseUrl = (serviceAddress: string): string => {
    // Our media-cache proxy already serves at the correct base
    return serviceAddress.replace(/\/+$/, "");
};

/**
 * Process API Image URL using reference logic
 */
export function getImageUrl(url: string | undefined | null, options: ImageOptions = {}): string | null {
    if (!url) return null;

    let inputUrl = url;

    // Handle legacy/dev domains (media.dev.paximum.com)
    if (inputUrl.includes('paximum.com') || inputUrl.includes('dev.paximum')) {
        inputUrl = inputUrl.replace(/^https?:\/\/[^\/]+/, 'https://b2cservice.lumitours.com/media');
    }

    // Direct /api/ relative paths should not be prepended with /api/media-cache
    if (inputUrl.startsWith('/api/') || inputUrl.startsWith('api/')) {
        return inputUrl.startsWith('/') ? inputUrl : '/' + inputUrl;
    }

    let fullUrl = inputUrl;

    // Logic from reference: processApiImage
    // Check if relative URL
    if (!inputUrl.toLowerCase().startsWith('http') && !inputUrl.startsWith('//')) {
        const mediaBase = getMediaBaseUrl(B2C_BASE);
        // Normalize slashes and remove leading ~ or /
        const cleanPath = inputUrl.replace(/\\/g, '/').replace(/^[~/]+/, ''); 
        // Tourvisio paths usually start with 'Media/Images', avoid duplicating 'media'
        const pathSuffix = cleanPath.toLowerCase().startsWith('media/') 
            ? cleanPath.substring(6) // Remove "Media/" or "media/"
            : cleanPath;
        fullUrl = `${mediaBase}/${pathSuffix}`;
    }

    // Bypass imgproc processing for tourvisio.com domains (SSL cert issues)
    if (fullUrl.includes('tourvisio.com/imgproc')) {
        return fullUrl;
    }

    const params = new URLSearchParams();
    if (options.width) params.append('width', options.width.toString());
    if (options.height) params.append('height', options.height.toString());

    // Map 'contain' -> 'pad' and 'cover' -> 'crop' if needed to match API expectations
    // Reference code passes specific modes directly, but let's keep the mapping for frontend compatibility
    let mode = options.mode;
    if (mode === 'contain') mode = 'pad';
    if (mode === 'cover') mode = 'crop';

    if (mode) params.append('mode', mode);
    if (options.anchor) params.append('anchor', options.anchor);
    if (options.quality) params.append('quality', options.quality.toString());
    if (options.bgcolor) params.append('bgcolor', options.bgcolor);

    const separator = fullUrl.includes('?') ? '&' : '?';
    const queryString = params.toString();

    return queryString ? `${fullUrl}${separator}${queryString}` : fullUrl;
}

/**
 * Helper helpers for compatibility
 */
export function fixImageUrl(url: string | undefined | null): string | null {
    // Current usages of fixImageUrl expect the base URL logic without params
    return getImageUrl(url);
}

/**
 * Strip existing image processing params (width, height, mode, quality)
 * from a URL so that they can be re-applied at the correct size.
 * This prevents double-sizing where a thumbnail URL gets hero params appended
 * but the original ?width=400 remains in the URL.
 */
export function stripImageParams(url: string | undefined | null): string | null {
    if (!url) return null;
    try {
        const urlObj = new URL(url);
        ['width', 'height', 'mode', 'quality', 'anchor', 'bgcolor', 'widthratio', 'heightratio'].forEach(p => urlObj.searchParams.delete(p));
        return urlObj.toString();
    } catch {
        // Not a full URL (relative path) — strip params manually
        const qIndex = url.indexOf('?');
        if (qIndex === -1) return url;
        const base = url.substring(0, qIndex);
        const params = new URLSearchParams(url.substring(qIndex + 1));
        ['width', 'height', 'mode', 'quality', 'anchor', 'bgcolor', 'widthratio', 'heightratio'].forEach(p => params.delete(p));
        const remaining = params.toString();
        return remaining ? `${base}?${remaining}` : base;
    }
}

export function getHeroImageUrl(url: string | undefined | null): string | null {
    // Strip any pre-existing size params before applying hero dimensions
    const cleanUrl = stripImageParams(url);
    return getImageUrl(cleanUrl, {
        width: 1920,
        height: 1080,
        mode: 'max', // Reference uses 'max' for strict fit
        quality: 100
    });
}

export function getThumbnailUrl(url: string | undefined | null, size: number = 400): string | null {
    return getImageUrl(url, {
        width: size,
        height: size,
        mode: 'crop',
        quality: 85
    });
}

export function getGalleryImageUrl(url: string | undefined | null): string | null {
    return getImageUrl(url, {
        width: 1024, // Increased to match reference usage
        mode: 'max',
        quality: 90
    });
}

export default {
    getImageUrl,
    fixImageUrl,
    getHeroImageUrl,
    getThumbnailUrl,
    getGalleryImageUrl,
    stripImageParams
};
